// V0 formulary — client-side seed of ~25 common primary-care medications
// prescribed in Nigerian clinics. Adult defaults only (paediatric dosing
// is a Week 5+ concern requiring weight-based calculation). Clinician can
// always adjust dose / frequency / duration after picking a preset.
//
// Kept client-side deliberately: this is data, not policy, and shipping a
// server-side formulary table with its own admin UI is scope creep for V0.
// If we outgrow this — real drug interactions, formulary versioning, WHO
// EML alignment — we migrate to a `formulary_entries` table then.
//
// Search is done by substring match over `name` + `aliases`. Case-insensitive.

export interface FormularyEntry {
    // Rendered in the picker + persisted verbatim as medication_name.
    name: string;
    // Extra strings that match search (brand names, common misspellings).
    aliases: string[];
    // Prefilled into the form when the entry is picked. Clinician can edit.
    dose: string;
    route: string;
    frequency: string;
    duration: string;
    instructions?: string;
    dispenseQty?: string;
    // Coarse category label, shown in the picker for context.
    category: 'Antimalarial' | 'Antibiotic' | 'Analgesic' | 'Antihypertensive'
        | 'Antidiabetic' | 'Antacid' | 'Antihelminthic' | 'Antihistamine'
        | 'Vitamin/Supplement' | 'ORS/Rehydration' | 'Other';
}

export const FORMULARY: FormularyEntry[] = [
    // Antimalarials
    { name: 'Artemether/Lumefantrine', aliases: ['Coartem', 'AL', 'Lumether'],
      dose: '80/480 mg', route: 'oral', frequency: 'twice daily',
      duration: '3 days', instructions: 'Take with fatty food or milk.',
      dispenseQty: '24 tablets', category: 'Antimalarial' },
    { name: 'Artesunate/Amodiaquine', aliases: ['ASAQ'],
      dose: '100/270 mg', route: 'oral', frequency: 'once daily',
      duration: '3 days', category: 'Antimalarial' },
    { name: 'Sulfadoxine/Pyrimethamine', aliases: ['SP', 'Fansidar'],
      dose: '1500/75 mg', route: 'oral', frequency: 'single dose',
      duration: '1 day', instructions: 'IPTp dose in pregnancy.',
      category: 'Antimalarial' },

    // Antibiotics
    { name: 'Amoxicillin', aliases: ['Amoxil'],
      dose: '500 mg', route: 'oral', frequency: 'three times daily',
      duration: '5 days', dispenseQty: '15 capsules', category: 'Antibiotic' },
    { name: 'Amoxicillin/Clavulanate', aliases: ['Augmentin', 'Co-amoxiclav'],
      dose: '625 mg', route: 'oral', frequency: 'three times daily',
      duration: '5 days', dispenseQty: '15 tablets', category: 'Antibiotic' },
    { name: 'Azithromycin', aliases: ['Zithromax', 'Zmax'],
      dose: '500 mg', route: 'oral', frequency: 'once daily',
      duration: '3 days', dispenseQty: '3 tablets', category: 'Antibiotic' },
    { name: 'Ciprofloxacin', aliases: ['Cipro'],
      dose: '500 mg', route: 'oral', frequency: 'twice daily',
      duration: '5 days', dispenseQty: '10 tablets', category: 'Antibiotic' },
    { name: 'Doxycycline', aliases: ['Vibramycin'],
      dose: '100 mg', route: 'oral', frequency: 'twice daily',
      duration: '7 days', dispenseQty: '14 capsules',
      instructions: 'Avoid dairy within 2 hours. Not in pregnancy.',
      category: 'Antibiotic' },
    { name: 'Metronidazole', aliases: ['Flagyl'],
      dose: '400 mg', route: 'oral', frequency: 'three times daily',
      duration: '7 days', dispenseQty: '21 tablets',
      instructions: 'Avoid alcohol.', category: 'Antibiotic' },

    // Analgesics / antipyretics
    { name: 'Paracetamol', aliases: ['Panadol', 'Acetaminophen', 'Emzor'],
      dose: '1 g', route: 'oral', frequency: 'four times daily as needed',
      duration: '3 days', dispenseQty: '12 tablets',
      instructions: 'Max 4 g in 24 hours.', category: 'Analgesic' },
    { name: 'Ibuprofen', aliases: ['Brufen', 'Advil'],
      dose: '400 mg', route: 'oral', frequency: 'three times daily as needed',
      duration: '3 days', dispenseQty: '9 tablets',
      instructions: 'Take with food. Avoid in ulcers or CKD.',
      category: 'Analgesic' },
    { name: 'Diclofenac', aliases: ['Voltaren', 'Cataflam'],
      dose: '50 mg', route: 'oral', frequency: 'twice daily',
      duration: '3 days', dispenseQty: '6 tablets', category: 'Analgesic' },

    // Antihypertensives
    { name: 'Amlodipine', aliases: ['Norvasc'],
      dose: '5 mg', route: 'oral', frequency: 'once daily',
      duration: '30 days', dispenseQty: '30 tablets', category: 'Antihypertensive' },
    { name: 'Lisinopril', aliases: ['Zestril', 'Prinivil'],
      dose: '10 mg', route: 'oral', frequency: 'once daily',
      duration: '30 days', dispenseQty: '30 tablets',
      instructions: 'Not in pregnancy.', category: 'Antihypertensive' },
    { name: 'Hydrochlorothiazide', aliases: ['HCTZ'],
      dose: '25 mg', route: 'oral', frequency: 'once daily',
      duration: '30 days', dispenseQty: '30 tablets', category: 'Antihypertensive' },
    { name: 'Nifedipine (extended-release)', aliases: ['Adalat'],
      dose: '30 mg', route: 'oral', frequency: 'once daily',
      duration: '30 days', dispenseQty: '30 tablets', category: 'Antihypertensive' },

    // Antidiabetics
    { name: 'Metformin', aliases: ['Glucophage'],
      dose: '500 mg', route: 'oral', frequency: 'twice daily with meals',
      duration: '30 days', dispenseQty: '60 tablets',
      instructions: 'Titrate up over 2 weeks.', category: 'Antidiabetic' },
    { name: 'Glibenclamide', aliases: ['Daonil'],
      dose: '5 mg', route: 'oral', frequency: 'once daily before breakfast',
      duration: '30 days', dispenseQty: '30 tablets', category: 'Antidiabetic' },

    // Antacids / GI
    { name: 'Omeprazole', aliases: ['Losec'],
      dose: '20 mg', route: 'oral', frequency: 'once daily before breakfast',
      duration: '14 days', dispenseQty: '14 capsules', category: 'Antacid' },
    { name: 'Ranitidine', aliases: ['Zantac'],
      dose: '150 mg', route: 'oral', frequency: 'twice daily',
      duration: '14 days', dispenseQty: '28 tablets', category: 'Antacid' },

    // Antihelminthics
    { name: 'Albendazole', aliases: ['Zentel'],
      dose: '400 mg', route: 'oral', frequency: 'single dose',
      duration: '1 day', dispenseQty: '1 tablet',
      instructions: 'Repeat in 2 weeks for pinworm.', category: 'Antihelminthic' },
    { name: 'Praziquantel', aliases: ['Biltricide'],
      dose: '40 mg/kg', route: 'oral', frequency: 'single dose',
      duration: '1 day', category: 'Antihelminthic' },

    // Antihistamines
    { name: 'Cetirizine', aliases: ['Zyrtec'],
      dose: '10 mg', route: 'oral', frequency: 'once daily',
      duration: '7 days', dispenseQty: '7 tablets', category: 'Antihistamine' },
    { name: 'Chlorpheniramine', aliases: ['Piriton'],
      dose: '4 mg', route: 'oral', frequency: 'three times daily',
      duration: '5 days', dispenseQty: '15 tablets',
      instructions: 'Causes drowsiness.', category: 'Antihistamine' },

    // Vitamins / supplements
    { name: 'Folic acid', aliases: [],
      dose: '5 mg', route: 'oral', frequency: 'once daily',
      duration: '90 days', dispenseQty: '90 tablets',
      instructions: 'Standard antenatal supplementation.',
      category: 'Vitamin/Supplement' },
    { name: 'Ferrous sulfate', aliases: ['Iron'],
      dose: '200 mg', route: 'oral', frequency: 'twice daily',
      duration: '30 days', dispenseQty: '60 tablets',
      instructions: 'Take with vitamin C, avoid tea within 1 hour.',
      category: 'Vitamin/Supplement' },
    { name: 'Vitamin C', aliases: ['Ascorbic acid'],
      dose: '500 mg', route: 'oral', frequency: 'once daily',
      duration: '30 days', dispenseQty: '30 tablets',
      category: 'Vitamin/Supplement' },

    // ORS
    { name: 'ORS (Oral Rehydration Salts)', aliases: ['ORS sachet'],
      dose: '1 sachet in 1 L water', route: 'oral',
      frequency: 'after each loose stool',
      duration: 'until rehydrated', dispenseQty: '5 sachets',
      category: 'ORS/Rehydration' },
];

// Case-insensitive substring search over name + aliases. Returns the first
// N matches (order preserved from FORMULARY array). Trims + lowercases the
// query once for efficiency.
export function searchFormulary(query: string, limit = 8): FormularyEntry[] {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const out: FormularyEntry[] = [];
    for (const e of FORMULARY) {
        if (out.length >= limit) break;
        if (e.name.toLowerCase().includes(q)) { out.push(e); continue; }
        if (e.aliases.some((a) => a.toLowerCase().includes(q))) out.push(e);
    }
    return out;
}
